import * as React from 'react';
import { Button } from '@/components/common/Button';
import { Textarea } from '@/components/common/Textarea';
import { Label } from '@/components/common/Label';
import { cn } from '@/utils/utils';
import { CircleCheck, CircleX, RotateCcw } from 'lucide-react';
import { TopicTypeApprove, TOPIC_TYPE_APPROVE } from '@/types/enums';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/hooks/useStore';
import { flowResult } from 'mobx';
import { toastify } from '@/utils/toastify';

interface ConfirmPopupProps {
    type: TopicTypeApprove,
    children?: React.ReactNode,
    triggerClassName?: string,
    disabled?: boolean
}

const ConfirmPopup: React.FC<ConfirmPopupProps> = ({ type, children, triggerClassName, disabled }) => {
    const { t } = useTranslation();
    const {
        topicStore: { topicDetail, updateApproveTopic, getTopicDetail }
    } = useStore();
    const {
        modalStore: { showAlertModal, hideModal },
        modalStore: { modalWaringAlertOptions }
    } = useStore();

    // Component riêng cho nội dung modal để dùng state
    const RejectModalContent: React.FC<{
        onClose: () => void,
        onSave: (reason: string) => void
    }> = ({ onClose, onSave }) => {
        const [reason, setReason] = React.useState('');

        return (
            <div className='flex flex-col items-start w-full'>
                <span>{t('words_title.are_you_sure_you_want_to_reject_this_post')}</span>
                <div className='flex items-start gap-4 mt-3 w-full'>
                    <Label className='label-1 w-[14%] flex'>{t('words_title.reason')}</Label>
                    <Textarea
                        className='flex-1 resize-none h-[133px]'
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                        maxLength={1000}

                    />
                </div>

                <div className='flex justify-center gap-6 mt-5 w-full'>
                    <Button className='w-1/2' variant='outline' onClick={onClose}>
                        {t('button.cancel')}
                    </Button>
                    <Button
                        onClick={() => onSave(reason)}
                        className='bg-[var(--active)] w-1/2 text-white hover:bg-[var(--active)]'
                    >
                        {t(`button.${type}`)}
                    </Button>
                </div>
            </div>
        );
    };

    const handleOpen = () => {
        if (type === TOPIC_TYPE_APPROVE.REJECT) {
            showAlertModal(
                modalWaringAlertOptions({
                    hideIconCenter: true,
                    footer: null,
                    cancelButton: false,
                    saveButton: false,
                    id: 'warning-modal',
                    content: (
                        <RejectModalContent
                            onClose={() => hideModal('warning-modal')}
                            onSave={async (reason) => {
                                if (topicDetail) {
                                    const payload = { typeApprove: type, reason };
                                    const res = await flowResult(updateApproveTopic(topicDetail.code, payload));
                                    if (res?.ok) toastify('success', t('messages.update_success'));
                                    await flowResult(getTopicDetail(topicDetail.code));
                                }
                                hideModal('warning-modal');
                            }}
                        />
                    )
                })
            );
        } else {
            // approve / republish simple confirm modal
            showAlertModal(
                modalWaringAlertOptions({
                    hideIconCenter: true,
                    cancelButton: <>{t('button.cancel')}</>,
                    saveButton: <>{t(`button.${type}`)}</>,
                    content: (
                        <span>
                            {type === TOPIC_TYPE_APPROVE.APPROVE ?
                                t('words_title.are_you_sure_you_want_to_approve_this_post') :
                                (
                                    <span className='flex items-start justify-center'>
                                        {t('words_title.are_you_sure_you_want_to_republish_this_post')}
                                        <br />
                                        {t('words_title.if_you_agree_the_post_will_be_reapproved_before_it_is_officially_published')}
                                    </span>
                                )}
                        </span>
                    ),
                    onSave: async () => {
                        if (topicDetail) {
                            const payload = { typeApprove: type, reason: '' };
                            const res = await flowResult(updateApproveTopic(topicDetail.code, payload));
                            if (res?.ok) toastify('success', t('messages.update_success'));
                            await flowResult(getTopicDetail(topicDetail.code));
                        }
                    }
                })
            );
        }
    };

    return (
        <>
            {children ?
                (
                    React.cloneElement(children as React.ReactElement, { onClick: handleOpen })
                ) :
                (
                    <Button
                        onClick={handleOpen}
                        type='button'
                        className={cn(
                            'w-full text-[15px] bg-transparent flex items-center gap-2 border-none shadow-none',
                            triggerClassName,
                            type === TOPIC_TYPE_APPROVE.REJECT ?
                                'text-[rgba(218,37,29,1)] hover:text-[rgba(218,37,29,1)]' :
                                type === TOPIC_TYPE_APPROVE.APPROVE ?
                                    'text-[rgba(7,125,7,1)] hover:text-[rgba(7,125,7,1)]' :
                                    'text-[rgba(238,135,0,1)] hover:text-[rgba(238,135,0,1)]'
                        )}
                        disabled={disabled}
                    >
                        {type === TOPIC_TYPE_APPROVE.REJECT && <CircleX size={17} color='rgba(218,37,29,1)' />}
                        {type === TOPIC_TYPE_APPROVE.APPROVE && <CircleCheck size={17} color='rgba(7,125,7,1)' />}
                        {type === TOPIC_TYPE_APPROVE.RE_PUBLIC && <RotateCcw size={17} color='rgba(238,135,0,1)' />}
                        <span>{t(`button.${type}`)}</span>
                    </Button>
                )}
        </>
    );
};

export default ConfirmPopup;
